import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { AuthState, User } from '../types';
import { authService } from '../services/api';

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, phoneNumber: string, password: string, first_name: string, last_name: string, tncAccepted?: boolean, tncAcceptedAt?: string) => Promise<void>;
  loginWithGoogle: (credential: string, tncAccepted?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (user: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'AUTH_FAILURE' }
  | { type: 'UPDATE_USER'; payload: Partial<User> }
  | { type: 'LOGOUT' };

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  console.log('AUTH REDUCER: Action received:', action.type);
  console.log('AUTH REDUCER: Current state:', state);
  
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
      };
    case 'AUTH_SUCCESS':
      const successState = {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
      };
      console.log('AUTH REDUCER: AUTH_SUCCESS new state:', successState);
      return successState;
    case 'AUTH_FAILURE':
      const failureState = {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      };
      console.log('AUTH REDUCER: AUTH_FAILURE new state:', failureState);
      return failureState;
    case 'UPDATE_USER':
      return state.user ? { ...state, user: { ...state.user, ...action.payload } } : state;
    case 'LOGOUT':
      const logoutState = {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      };
      console.log('AUTH REDUCER: LOGOUT new state:', logoutState);
      return logoutState;
    default:
      return state;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    // Check if user is logged in on app start
    const token = localStorage.getItem('token');
    if (token) {
      // Verify token with backend and get user info
      verifyToken(token);
    }
  }, []);

  const verifyToken = async (token: string) => {
    try {
      dispatch({ type: 'AUTH_START' });
      
      // Use the existing profile endpoint to verify token and get user info
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'}/api/auth/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: { user: data.user, token },
        });
      } else {
        // Token is invalid, remove it
        localStorage.removeItem('token');
        dispatch({ type: 'AUTH_FAILURE' });
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      localStorage.removeItem('token');
      dispatch({ type: 'AUTH_FAILURE' });
    }
  };

  const login = async (email: string, password: string) => {
    try {
      dispatch({ type: 'AUTH_START' });
      const response = await authService.login(email, password);
      
      localStorage.setItem('token', response.token);
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user: response.user, token: response.token },
      });
    } catch (error) {
      dispatch({ type: 'AUTH_FAILURE' });
      throw error;
    }
  };

  const loginWithGoogle = async (credential: string, tncAccepted = false) => {
    try {
      dispatch({ type: 'AUTH_START' });
      const response = await authService.loginWithGoogle(credential, tncAccepted);

      localStorage.setItem('token', response.token);
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user: response.user, token: response.token },
      });
    } catch (error) {
      dispatch({ type: 'AUTH_FAILURE' });
      throw error;
    }
  };

  const register = async (email: string, phoneNumber: string, password: string, first_name: string, last_name: string, tncAccepted = false, tncAcceptedAt?: string) => {
    try {
      dispatch({ type: 'AUTH_START' });
      const response = await authService.register(email, phoneNumber, password, first_name, last_name, tncAccepted, tncAcceptedAt);

      localStorage.setItem('token', response.token);
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user: response.user, token: response.token },
      });
    } catch (error) {
      dispatch({ type: 'AUTH_FAILURE' });
      throw error;
    }
  };

  const logout = async () => {
    console.log('AUTH: Logout function called');
    
    try {
      // Call backend logout endpoint if token exists
      const token = localStorage.getItem('token');
      console.log('AUTH: Token before logout:', token);
      
      if (token) {
        console.log('AUTH: Calling backend logout endpoint');
        await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        console.log('AUTH: Backend logout call completed');
      }
    } catch (error) {
      console.error('AUTH: Backend logout failed:', error);
      // Continue with client-side logout even if backend call fails
    }
    
    // Clear all authentication data
    console.log('AUTH: Clearing localStorage');
    localStorage.removeItem('token');
    localStorage.removeItem('user'); // In case user data is cached separately
    
    // Clear any other auth-related items that might be stored
    const authKeys = ['authToken', 'userToken', 'sessionToken'];
    authKeys.forEach(key => localStorage.removeItem(key));
    
    console.log('AUTH: Dispatching LOGOUT action');
    // Dispatch logout action
    dispatch({ type: 'LOGOUT' });
    
    console.log('AUTH: Logout completed - token cleared from localStorage');
    console.log('AUTH: Token after clear:', localStorage.getItem('token'));
  };

  const value: AuthContextType = {
    ...state,
    login,
    register,
    loginWithGoogle,
    logout,
    updateUser: (user: Partial<User>) => dispatch({ type: 'UPDATE_USER', payload: user }),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

